import { useState, useMemo, Suspense, startTransition } from "react";
import {
  privateAgent,
  Button,
  DialogBox,
  TextButton,
  AlertDialog,
  useTitle,
} from "../../../lib";
import { DataTable } from "../../../lib/Forms/DataTable";
import { MRT_ColumnDef } from "material-react-table";
import { Formik, Form } from "formik";
import { useQuery, useQueryClient } from "react-query";
import { routesName } from "../../../lib/RoutesName/Routes";
import { Mode } from "../../../components/shared/dropdown/mode";
import { useSnackbar } from "notistack";
import ClassModeMenu from "../../../components/formFields/Classes/ClassModeMenu";
import { AddBehaviorCategoryProps, BehaviorCategoryProps } from "../../../lib/Types/BehaviorCategoryProps";
import AddBehaviorCategoryValidationSchema from "../../../components/formFields/Behaviors/AddBehaviorCategoryValidationSchema";
import AddBehaviorCategoryFields from "../../../components/formFields/Behaviors/AddBehaviorCategoryFields";
import {
  behaviorCategoryInitialValues,
  editBehaviorCategoryIntitialValues,
} from "../../../components/formFields/Behaviors/behavior-helpers";
import {
  ButtonContainer,
  TopContainerNoMargin,
  UsersContainer,
  TableContainer,
} from "src/styles/Containers";
import { TitleLarge } from "src/styles/Typography";
import { useBehaviorCategory } from "@lib/Hooks/behavior/behavior.category.hook";
import HoverMenu, { BehaviorCatMenuOptions, CommandContext, DeleteCommand, DuplicateCommand, EditCommand } from "@components/shared/HoverMenu";
import PaigeSkeleton from "@components/formFields/skeleton/PaigeSkeleton";

function BehaviorCategoryPage() {
  useTitle("Behavior Categories | Paige");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [openAddBehaviorCategoryDialog, setOpenAddBehaviorCategoryDialog] =
    useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [behaviorCatId, setBehaviorCatId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [fieldInitialValues, setFieldInitialValues] =
    useState<AddBehaviorCategoryProps>(behaviorCategoryInitialValues);
  const queryClient = useQueryClient();

  let { isLoading, data, addBehaviorCatMutation, updateBehaviorCatMutation, deleteBehaviorCatMutation } = useBehaviorCategory();

  const handleSubmit = async (values: AddBehaviorCategoryProps) => {
    if (isEdit) {
      await updateBehaviorCatMutation.mutateAsync({ id: behaviorCatId, data: values });
      setOpenAddBehaviorCategoryDialog(false);
    } else {
      await addBehaviorCatMutation.mutateAsync({ id: behaviorCatId, data: values });
      setOpenAddBehaviorCategoryDialog(false);
    }
  };

  const handleDelete = async () => {
    await deleteBehaviorCatMutation.mutateAsync(behaviorCatId);
    setOpenDeleteDialog(false);
  };

  const handleRowClick = (column: any, row: any) => {
    if (column.id === "name") {
      startTransition(() => {
        setOpenAddBehaviorCategoryDialog(true);
        setFieldInitialValues(editBehaviorCategoryIntitialValues(row.original));
        setBehaviorCatId(row.original.id);
        setIsEdit(true);
      });
    }
  };

  //grid defn
  const columns = useMemo<MRT_ColumnDef<BehaviorCategoryProps>[]>(
    () => [
      {
        accessorFn: (row) => `${row.name}`,
        id: "name",
        header: "Name",
        minSize: 150,
      },
      {
        id: "actions",
        header: "Actions",
        headerClassName: "header-row",
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
          className: "action",
        },
        size: 50,
        enableResizing: false, //disable resizing for this column
        Cell: ({ cell }) => {
          const context: CommandContext<BehaviorCategoryProps> = {
            setOpenDialog: setOpenAddBehaviorCategoryDialog,
            setFieldInitialValues,
            setId: setBehaviorCatId,
            setIsEdit,
            row: cell.row.original,
          };

          const commands = {
            edit: new EditCommand(context),
            duplicate: new DuplicateCommand(context),
            delete: new DeleteCommand({
              ...context,
              setOpenDialog: setOpenDeleteDialog,
            }),
          };

          return <HoverMenu id={0} commands={commands} options={BehaviorCatMenuOptions} />
        },
      },
    ],
    []
  );
  //end of grid defn

  return (
    <>
      <AlertDialog
        variety='error'
        onConfirm={() => handleDelete()}
        onClose={() => setOpenDeleteDialog(false)}
        isOpen={openDeleteDialog}
        title={"Are you sure you want to delete this class?"}
        content={
          "Deleting this class will permanently erase any data associated with this class."
        }
        btnText={{ cancel: "Cancel", action: "Delete" }}
      />
      <DialogBox
        isOpen={openAddBehaviorCategoryDialog}
        onClose={() => setOpenAddBehaviorCategoryDialog(false)}
        title={isEdit ? "Edit Behavior Category" : "Add Behavior Category"}
        content={
          <Formik
            initialValues={fieldInitialValues}
            onSubmit={handleSubmit}
            validationSchema={AddBehaviorCategoryValidationSchema}
          >
            {(formikProps) => (
              <Form>
                <AddBehaviorCategoryFields />
                <ButtonContainer>
                  <TextButton
                    label={"Cancel"}
                    onClick={() => setOpenAddBehaviorCategoryDialog(false)}
                  ></TextButton>
                  <Button
                    label={"Save"}
                    type={"submit"}
                    disabled={!formikProps.isValid}
                    loading={addBehaviorCatMutation.isLoading || updateBehaviorCatMutation.isLoading}
                  ></Button>
                </ButtonContainer>
              </Form>
            )}
          </Formik>
        }
      />
      <TopContainerNoMargin>
        <TitleLarge>Behavior Categories</TitleLarge>
        <Button
          label={"+ Add Behavior Category"}
          onClick={() => {
            startTransition(() => {
              setOpenAddBehaviorCategoryDialog(true);
              setIsEdit(false);
            });
          }}
        ></Button>
      </TopContainerNoMargin>
      <UsersContainer>
        <Suspense fallback={<PaigeSkeleton />}>
          <TableContainer>
            {data && data.length > 0 && (
              <DataTable
                columns={columns}
                data={data}
                onCellClick={handleRowClick}
              />
            )}
          </TableContainer>
        </Suspense>
      </UsersContainer>
    </>
  );
}

export default BehaviorCategoryPage;
